// ============================================================================
// BrightPath - usePushNotifications Hook
// BP-12-03: Notifications Frontend
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../api/notificationsApi';
import { useNotificationStore } from '../stores/notificationStore';
import { notificationKeys } from './useNotifications';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface UsePushNotificationsReturn {
  // State
  isSupported: boolean;
  isEnabled: boolean;
  isSubscribed: boolean;
  permissionStatus: NotificationPermission | null;
  
  // Loading states
  isLoading: boolean;
  isEnabling: boolean;
  isDisabling: boolean;
  
  // Actions
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  requestPermission: () => Promise<NotificationPermission>;
  
  // Error
  error: Error | null;
}

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

// ----------------------------------------------------------------------------
// Utilities
// ----------------------------------------------------------------------------

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Check if push notifications are supported
 */
function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get the active service worker registration
 */
async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    // Try to get existing registration
    let registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      // Register the service worker if not registered
      registration = await navigator.serviceWorker.register('/sw-notifications.js', {
        scope: '/',
      });
    }

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    console.error('Failed to get service worker registration:', error);
    return null;
  }
}

/**
 * Get existing push subscription
 */
async function getExistingSubscription(): Promise<PushSubscription | null> {
  const registration = await getServiceWorkerRegistration();
  if (!registration) return null;

  try {
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Failed to get existing subscription:', error);
    return null;
  }
}

// ----------------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------------

export function usePushNotifications(): UsePushNotificationsReturn {
  const queryClient = useQueryClient();
  const store = useNotificationStore();
  
  const [error, setError] = useState<Error | null>(null);
  const [isSupported] = useState(() => isPushSupported());

  // --------------------------------------------------------------------------
  // Queries
  // --------------------------------------------------------------------------

  // Check server-side subscription status
  const subscriptionStatusQuery = useQuery({
    queryKey: notificationKeys.pushStatus(),
    queryFn: async () => {
      const response = await notificationsApi.getPushSubscriptionStatus();
      return response.data;
    },
    enabled: isSupported,
    staleTime: 60000, // 1 minute
  });

  // --------------------------------------------------------------------------
  // Effects
  // --------------------------------------------------------------------------

  // Check and sync permission status on mount
  useEffect(() => {
    if (!isSupported) return;

    const checkPermission = () => {
      const permission = Notification.permission;
      store.setPushPermissionStatus(permission);
      
      if (permission === 'granted') {
        // Check if we have an active subscription
        getExistingSubscription().then((subscription) => {
          store.setPushEnabled(!!subscription);
        });
      }
    };

    checkPermission();
  }, [isSupported, store]);

  // Sync with server status
  useEffect(() => {
    if (subscriptionStatusQuery.data) {
      store.setPushEnabled(subscriptionStatusQuery.data.subscribed);
    }
  }, [subscriptionStatusQuery.data, store]);

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  // Subscribe to push notifications
  const subscribeMutation = useMutation({
    mutationFn: async () => {
      if (!VAPID_PUBLIC_KEY) {
        throw new Error('VAPID public key not configured');
      }

      const registration = await getServiceWorkerRegistration();
      if (!registration) {
        throw new Error('Service worker not available');
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Extract keys
      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('Invalid subscription data');
      }

      // Send to server
      await notificationsApi.subscribeToPush({
        endpoint: json.endpoint,
        p256dhKey: json.keys.p256dh,
        authKey: json.keys.auth,
        deviceType: 'web',
      });

      return subscription;
    },
    onSuccess: () => {
      store.setPushEnabled(true);
      queryClient.invalidateQueries({ queryKey: notificationKeys.pushStatus() });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
      store.setPushEnabled(false);
    },
  });

  // Unsubscribe from push notifications
  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      // Unsubscribe from browser
      const subscription = await getExistingSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Unsubscribe from server
      await notificationsApi.unsubscribeFromPush();
    },
    onSuccess: () => {
      store.setPushEnabled(false);
      queryClient.invalidateQueries({ queryKey: notificationKeys.pushStatus() });
      setError(null);
    },
    onError: (err) => {
      setError(err as Error);
    },
  });

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      store.setPushPermissionStatus(permission);
      return permission;
    } catch (err) {
      console.error('Failed to request notification permission:', err);
      return 'denied';
    }
  }, [isSupported, store]);

  const enable = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Push notifications are not supported');
    }

    // Request permission if not granted
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await requestPermission();
    }

    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    // Subscribe
    await subscribeMutation.mutateAsync();
  }, [isSupported, requestPermission, subscribeMutation]);

  const disable = useCallback(async () => {
    await unsubscribeMutation.mutateAsync();
  }, [unsubscribeMutation]);

  // --------------------------------------------------------------------------
  // Return
  // --------------------------------------------------------------------------

  return {
    isSupported,
    isEnabled: store.pushEnabled,
    isSubscribed: subscriptionStatusQuery.data?.subscribed ?? false,
    permissionStatus: store.pushPermissionStatus,
    
    isLoading: subscriptionStatusQuery.isLoading,
    isEnabling: subscribeMutation.isPending,
    isDisabling: unsubscribeMutation.isPending,
    
    enable,
    disable,
    requestPermission,
    
    error,
  };
}

export default usePushNotifications;

